import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  Snackbar,
  Alert,
  InputAdornment,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Sync, Search, FileDownload } from "@mui/icons-material";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import {
  getOrderTypeName,
  getSaleChannelName,
  getStatusText,
  getStatusColor,
  getStatusId,
} from "./nhanhConfig";
import { exportOrdersToExcel } from "./nhanhExcelExporter";
import { fetchDepotsApi, fetchProductsApi, fetchOrdersApi } from "./nhanhApi";

const NHANH_APP_ID = process.env.REACT_APP_NHANH_APP_ID;
const NHANH_BUSINESS_ID = process.env.REACT_APP_NHANH_BUSINESS_ID;
const NHANH_ACCESS_TOKEN = process.env.REACT_APP_NHANH_ACCESS_TOKEN;

function NhanhOrders() {
  const [orders, setOrders] = useState([]);
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState([
    "Đang chuyển",
    "Thành công",
  ]);
  const [depotsMap, setDepotsMap] = useState({});
  const [productsCatalogMap, setProductsCatalogMap] = useState({});
  const [invoiceDataMap, setInvoiceDataMap] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(new Set());

  // Alert system state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  const triggerAlert = (message, severity = "success") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  // Call Nhanh API to fetch business depots list
  const fetchDepots = useCallback(async () => {
    try {
      const result = await fetchDepotsApi({
        appId: NHANH_APP_ID,
        businessId: NHANH_BUSINESS_ID,
        accessToken: NHANH_ACCESS_TOKEN,
      });

      console.log("Nhanh Business Depot result:", result);
      if (result && result.data) {
        let list = [];
        if (Array.isArray(result.data)) {
          list = result.data;
        } else if (result.data && Array.isArray(result.data.depots)) {
          list = result.data.depots;
        } else if (result.data && typeof result.data === "object") {
          list = Object.values(result.data);
        }

        const map = {};
        list.forEach((item) => {
          if (item && typeof item === "object") {
            const dId = String(item.id || item.depotId || "");
            const dName = item.name || item.depotName || "";
            if (dId) {
              map[dId] = dName;
            }
          }
        });
        setDepotsMap(map);
      }
    } catch (err) {
      console.error("Lỗi lấy danh sách kho hàng:", err.message);
    }
  }, []);

  // Call Nhanh API to fetch product catalog list
  const fetchProducts = useCallback(async () => {
    try {
      let resultData = null;
      const result = await fetchProductsApi({
        appId: NHANH_APP_ID,
        businessId: NHANH_BUSINESS_ID,
        accessToken: NHANH_ACCESS_TOKEN,
      });
      if (result && result.data) resultData = result.data;

      if (resultData) {
        const rawCatalogList = [];
        const extractItems = (obj) => {
          if (!obj) return;
          if (Array.isArray(obj)) {
            obj.forEach((item) => {
              if (item && typeof item === "object") {
                if (
                  item.code ||
                  item.productCode ||
                  item.sku ||
                  item.id ||
                  item.name
                ) {
                  rawCatalogList.push(item);
                }
                if (
                  item.products ||
                  item.children ||
                  item.items ||
                  item.combos
                ) {
                  extractItems(
                    item.products || item.children || item.items || item.combos,
                  );
                }
              }
            });
          } else if (typeof obj === "object") {
            Object.values(obj).forEach((item) => {
              if (item && typeof item === "object") {
                if (
                  item.code ||
                  item.productCode ||
                  item.sku ||
                  item.id ||
                  item.name
                ) {
                  rawCatalogList.push(item);
                }
                if (
                  item.products ||
                  item.children ||
                  item.items ||
                  item.combos
                ) {
                  extractItems(
                    item.products || item.children || item.items || item.combos,
                  );
                }
              }
            });
          }
        };

        extractItems(resultData);

        const map = {};
        rawCatalogList.forEach((item) => {
          if (item && typeof item === "object") {
            const prices = item.prices || {};
            const retailP = Number(
              prices.retail ??
                prices.retailVat ??
                item.price ??
                item.salePrice ??
                item.retailPrice ??
                0,
            );
            const importP = Number(
              prices.import ??
                prices.avgCost ??
                prices.importVat ??
                item.originalPrice ??
                item.importPrice ??
                item.costPrice ??
                retailP,
            );
            const vatVal = Number(
              item.vat ?? item.vatRate ?? item.taxRate ?? 0,
            );
            const info = {
              price:
                !Number.isNaN(retailP) && retailP > 0
                  ? retailP
                  : !Number.isNaN(importP)
                    ? importP
                    : 0,
              originalPrice:
                !Number.isNaN(importP) && importP > 0
                  ? importP
                  : !Number.isNaN(retailP)
                    ? retailP
                    : 0,
              vat: !Number.isNaN(vatVal) ? vatVal : 0,
              code: String(
                item.code || item.productCode || item.sku || "",
              ).trim(),
              name: String(item.name || item.productName || "").trim(),
            };

            const keys = [
              item.code,
              item.productCode,
              item.sku,
              item.id,
              item.idNhanh,
              item.barcode,
              item.name,
              item.productName,
            ];

            keys.forEach((k) => {
              if (k !== undefined && k !== null && k !== "") {
                const strK = String(k).trim();
                if (strK) {
                  map[strK] = info;
                  map[strK.toLowerCase()] = info;
                  map[strK.toUpperCase()] = info;
                  map[strK.replace(/\s+/g, "")] = info;
                  map[strK.replace(/\s+/g, "").toLowerCase()] = info;
                }
              }
            });
          }
        });
        setProductsCatalogMap(map);
      }
    } catch (err) {
      console.error("Lỗi lấy danh sách sản phẩm:", err);
    }
  }, []);

  // Call Nhanh API to fetch order list (loads all pages in background)
  const handleFetchOrders = useCallback(async () => {
    setIsLoading(true);
    setSelectedKeys(new Set());
    fetchDepots();
    fetchProducts();

    try {
      const statusIds = statusFilter
        .map((name) => getStatusId(name))
        .filter((id) => id !== undefined && id !== null);

      let allMappedOrders = [];
      let nextId = null;

      do {
        const result = await fetchOrdersApi({
          appId: NHANH_APP_ID,
          businessId: NHANH_BUSINESS_ID,
          accessToken: NHANH_ACCESS_TOKEN,
          fromDate: fromDate ? fromDate.startOf("day").unix() : "",
          toDate: toDate ? toDate.endOf("day").unix() : "",
          statuses: statusIds,
          size: 100,
          nextId: nextId,
        });
        console.log("result page", allMappedOrders.length, result);

        if (result && result.data) {
          let rawOrders = [];
          if (Array.isArray(result.data)) {
            rawOrders = result.data;
          } else if (result.data && Array.isArray(result.data.orders)) {
            rawOrders = result.data.orders;
          } else if (
            result.data &&
            result.data.orders &&
            typeof result.data.orders === "object"
          ) {
            rawOrders = Object.values(result.data.orders);
          } else if (result.data && typeof result.data === "object") {
            rawOrders = Object.values(result.data);
          }

          const mappedOrders = rawOrders.map((o) => {
            const nhanhId = String(o.info?.id || o.id || "");
            const sanId = String(
              o.channel?.appOrderId || o.appOrderId || o.shopOrderId || "",
            );
            const rawDate = o.info?.createdAt || o.createdAt;

            let dateStr = dayjs().toISOString();
            if (rawDate) {
              const numDate = Number(rawDate);
              if (!Number.isNaN(numDate)) {
                if (numDate < 100000000000) {
                  dateStr = dayjs(numDate * 1000).toISOString();
                } else {
                  dateStr = dayjs(numDate).toISOString();
                }
              } else {
                dateStr = dayjs(rawDate).toISOString();
              }
            }

            return {
              id: nhanhId,
              shopOrderId: sanId,
              date: dateStr,
              customer:
                o.shippingAddress?.name ||
                o.customerName ||
                o.customer ||
                "Khách hàng Nhanh",
              phone:
                o.shippingAddress?.phone || o.customerMobile || o.phone || "",
              total: Number(
                o.payment?.codAmount ||
                  o.payment?.businessPayment ||
                  o.calcTotalMoney ||
                  o.totalMoney ||
                  o.total ||
                  0,
              ),
              status: o.info?.status ?? o.status ?? "Mới",
              raw: o,
            };
          });

          allMappedOrders = [...allMappedOrders, ...mappedOrders];
          // Update UI progressively as pages load
          setOrders([...allMappedOrders]);

          nextId = result.paginator?.next?.id || result.data?.paginator?.next?.id || null;
        } else {
          break;
        }
      } while (nextId);

      triggerAlert(`Lấy ${allMappedOrders.length} đơn hàng từ Nhanh.vn thành công!`);
    } catch (error) {
      console.error("Lỗi kết nối Nhanh API:", error);
      triggerAlert("Lấy đơn hàng thất bại!", "error");
    } finally {
      setIsLoading(false);
    }
  }, [fromDate, toDate, statusFilter, fetchDepots, fetchProducts]);

  const handleImportExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (orders.length === 0) {
      triggerAlert(
        "Đơn hàng đang trống, vui lòng Lấy Đơn Hàng trước khi import file excel!",
        "error",
      );
      e.target.value = null; // reset
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);

        setInvoiceDataMap((prev) => {
          const newMap = { ...prev };
          data.forEach((row) => {
            const nhanhId = String(row["ID"] || "");
            if (nhanhId) {
              newMap[nhanhId] = {
                invoiceNumber: row["Số hóa đơn điện tử"] || "",
                invoiceDate: row["Ngày xuất hóa đơn điện tử"] || "",
              };
            }
          });
          return newMap;
        });
        triggerAlert("Import thông tin hóa đơn thành công!", "success");
      } catch (error) {
        console.error("Lỗi đọc file excel:", error);
        triggerAlert("Có lỗi xảy ra khi đọc file Excel", "error");
      }
      e.target.value = null; // reset input
    };
    reader.readAsBinaryString(file);
  };

  // Auto fetch orders on mount or when filters change
  useEffect(() => {
    handleFetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, statusFilter]);

  // Filter orders based on user inputs
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      // 1. Search term (case-insensitive)
      const query = search.toLowerCase();
      const matchSearch =
        order.id.toLowerCase().includes(query) ||
        order.customer.toLowerCase().includes(query) ||
        order.phone.includes(query);

      // 2. Status filter
      const matchStatus =
        statusFilter.includes("all") ||
        statusFilter.includes(getStatusText(order.status));

      return matchSearch && matchStatus;
    });
  }, [orders, search, statusFilter]);


  const toggleSelectRow = (id) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (isChecked) => {
    if (isChecked) {
      const next = new Set(selectedKeys);
      filteredOrders.forEach((r) => next.add(r.id));
      setSelectedKeys(next);
    } else {
      const next = new Set(selectedKeys);
      filteredOrders.forEach((r) => next.delete(r.id));
      setSelectedKeys(next);
    }
  };

  const isAllSelected =
    filteredOrders.length > 0 && filteredOrders.every((r) => selectedKeys.has(r.id));
  const isSomeSelected =
    filteredOrders.some((r) => selectedKeys.has(r.id)) && !isAllSelected;

  return (
    <Box className="NhanhOrders" sx={{ padding: "0 2%", marginTop: "30px" }}>
      {/* Title */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: "bold", color: "#3F51B5" }}>
          Đồng bộ Đơn hàng Nhanh.vn
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={
              isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Sync />
              )
            }
            disabled={isLoading}
            onClick={handleFetchOrders}
            sx={{
              backgroundColor: "#3F51B5",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#303F9F" },
            }}
          >
            Lấy Đơn Hàng
          </Button>
          <Button
            variant="outlined"
            color="success"
            component="label"
            sx={{ fontWeight: 600 }}
          >
            Import Hóa Đơn
            <input
              type="file"
              accept=".xlsx, .xls"
              hidden
              onChange={handleImportExcel}
            />
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FileDownload />}
            disabled={
              isLoading ||
              filteredOrders.length === 0 ||
              selectedKeys.size === 0
            }
            onClick={() =>
              exportOrdersToExcel({
                orders,
                selectedKeys,
                productsCatalogMap,
                depotsMap,
                invoiceDataMap,
                triggerAlert,
              })
            }
            sx={{ fontWeight: 600 }}
          >
            Xuất Excel
          </Button>
        </Box>
      </Box>

      {/* Filters Area */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Tìm kiếm nhanh..."
              placeholder="Mã đơn hàng trên Nhanh"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.5}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Từ ngày"
                value={fromDate}
                format="DD-MM-YYYY"
                maxDate={toDate}
                onChange={(val) => {
                  setFromDate(val);
                }}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={2.5}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Đến ngày"
                value={toDate}
                format="DD-MM-YYYY"
                minDate={fromDate}
                onChange={(val) => {
                  setToDate(val);
                }}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Select
              multiple
              fullWidth
              size="small"
              value={statusFilter}
              onChange={(e) => {
                const value = e.target.value;
                if (value.includes("all")) {
                  if (statusFilter.includes("all")) {
                    const filtered = value.filter((v) => v !== "all");
                    setStatusFilter(filtered.length ? filtered : ["all"]);
                  } else {
                    setStatusFilter(["all"]);
                  }
                } else {
                  setStatusFilter(value.length ? value : ["all"]);
                }
              }}
              renderValue={(selected) => {
                if (selected.includes("all")) return "Trạng thái đơn: Tất cả";
                return `Trạng thái: ${selected.join(", ")}`;
              }}
            >
              <MenuItem value="all">
                <Checkbox
                  checked={statusFilter.includes("all")}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                Trạng thái đơn: Tất cả
              </MenuItem>
              <MenuItem value="Đơn mới">
                <Checkbox
                  checked={statusFilter.includes("Đơn mới")}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                Đơn mới
              </MenuItem>
              <MenuItem value="Đang xác nhận">
                <Checkbox
                  checked={statusFilter.includes("Đang xác nhận")}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                Đang xác nhận
              </MenuItem>
              <MenuItem value="Đã xác nhận">
                <Checkbox
                  checked={statusFilter.includes("Đã xác nhận")}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                Đã xác nhận
              </MenuItem>
              <MenuItem value="Chờ khách xác nhận">
                <Checkbox
                  checked={statusFilter.includes("Chờ khách xác nhận")}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                Chờ khách xác nhận
              </MenuItem>
              <MenuItem value="Đang đóng gói">
                <Checkbox
                  checked={statusFilter.includes("Đang đóng gói")}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                Đang đóng gói
              </MenuItem>
              <MenuItem value="Đã đóng gói">
                <Checkbox
                  checked={statusFilter.includes("Đã đóng gói")}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                Đã đóng gói
              </MenuItem>
              <MenuItem value="Chờ thu gom">
                <Checkbox
                  checked={statusFilter.includes("Chờ thu gom")}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                Chờ thu gom
              </MenuItem>
              <MenuItem value="Đang chuyển">
                <Checkbox
                  checked={statusFilter.includes("Đang chuyển")}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                Đang chuyển
              </MenuItem>
              <MenuItem value="Thành công">
                <Checkbox
                  checked={statusFilter.includes("Thành công")}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                Thành công
              </MenuItem>
              <MenuItem value="Thất bại">
                <Checkbox
                  checked={statusFilter.includes("Thất bại")}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                Thất bại
              </MenuItem>
              <MenuItem value="Khách hủy">
                <Checkbox
                  checked={statusFilter.includes("Khách hủy")}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                Khách hủy
              </MenuItem>
              <MenuItem value="Hãng vận chuyển hủy đơn">
                <Checkbox
                  checked={statusFilter.includes("Hãng vận chuyển hủy đơn")}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                Hãng vận chuyển hủy đơn
              </MenuItem>
              <MenuItem value="Hệ thống hủy">
                <Checkbox
                  checked={statusFilter.includes("Hệ thống hủy")}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                Hệ thống hủy
              </MenuItem>
              <MenuItem value="Hết hàng">
                <Checkbox
                  checked={statusFilter.includes("Hết hàng")}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                Hết hàng
              </MenuItem>
              <MenuItem value="Đang chuyển hoàn">
                <Checkbox
                  checked={statusFilter.includes("Đang chuyển hoàn")}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                Đang chuyển hoàn
              </MenuItem>
              <MenuItem value="Đã chuyển hoàn">
                <Checkbox
                  checked={statusFilter.includes("Đã chuyển hoàn")}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                Đã chuyển hoàn
              </MenuItem>
              <MenuItem value="Xác nhận hoàn">
                <Checkbox
                  checked={statusFilter.includes("Xác nhận hoàn")}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                Xác nhận hoàn
              </MenuItem>
            </Select>
          </Grid>
        </Grid>
      </Paper>

      {/* Orders Table */}
      <Paper
        elevation={2}
        sx={{ width: "100%", overflow: "hidden", position: "relative" }}
      >
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.7)",
              zIndex: 2,
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Đang tải dữ liệu đơn hàng từ Nhanh.vn...
            </Typography>
          </Box>
        )}

        <TableContainer
          sx={{ maxHeight: "calc(100vh - 430px)", minHeight: 300 }}
        >
          <Table stickyHeader aria-label="nhanh orders table">
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  style={{ backgroundColor: "#3F51B5", color: "#fff" }}
                >
                  <Checkbox
                    indeterminate={isSomeSelected}
                    checked={isAllSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                    sx={{
                      color: "#fff",
                      "&.Mui-checked": { color: "#fff" },
                      "&.MuiCheckbox-indeterminate": { color: "#fff" },
                    }}
                  />
                </TableCell>
                <TableCell
                  style={{
                    fontWeight: 600,
                    color: "#fff",
                    backgroundColor: "#3F51B5",
                  }}
                >
                  Mã Đơn Hàng Sàn
                </TableCell>
                <TableCell
                  style={{
                    fontWeight: 600,
                    color: "#fff",
                    backgroundColor: "#3F51B5",
                  }}
                >
                  Mã Đơn Hàng Nhanh
                </TableCell>
                <TableCell
                  style={{
                    fontWeight: 600,
                    color: "#fff",
                    backgroundColor: "#3F51B5",
                  }}
                >
                  Thời Gian Tạo
                </TableCell>
                <TableCell
                  style={{
                    fontWeight: 600,
                    color: "#fff",
                    backgroundColor: "#3F51B5",
                  }}
                >
                  Khách Hàng
                </TableCell>
                <TableCell
                  align="right"
                  style={{
                    fontWeight: 600,
                    color: "#fff",
                    backgroundColor: "#3F51B5",
                  }}
                >
                  Tổng Số Tiền
                </TableCell>
                <TableCell
                  align="center"
                  style={{
                    fontWeight: 600,
                    color: "#fff",
                    backgroundColor: "#3F51B5",
                  }}
                >
                  Trạng Thái Đơn Hàng
                </TableCell>
                <TableCell
                  align="center"
                  style={{
                    fontWeight: 600,
                    color: "#fff",
                    backgroundColor: "#3F51B5",
                  }}
                >
                  Ngày Hóa Đơn
                </TableCell>
                <TableCell
                  align="center"
                  style={{
                    fontWeight: 600,
                    color: "#fff",
                    backgroundColor: "#3F51B5",
                  }}
                >
                  Số Hóa Đơn
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" style={{ height: 260 }}>
                    <Typography color="textSecondary">
                      {isLoading
                        ? ""
                        : "Không tìm thấy đơn hàng nào khớp với điều kiện lọc"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrders.map((row) => {
                  const isChecked = selectedKeys.has(row.id);
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={row.id}
                      selected={isChecked}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isChecked}
                          onChange={() => toggleSelectRow(row.id)}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {row.shopOrderId || "-"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#3F51B5" }}>
                        {row.id}
                      </TableCell>
                      <TableCell>
                        {dayjs(row.date).format("DD/MM/YYYY HH:mm")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {row.customer}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {row.total.toLocaleString("vi-VN")} đ
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getStatusText(row.status)}
                          size="small"
                          color={getStatusColor(row.status)}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 500 }}>
                        {invoiceDataMap[row.id]?.invoiceDate || "-"}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 500 }}>
                        {invoiceDataMap[row.id]?.invoiceNumber || "-"}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            px: 2,
            py: 1.5,
            borderTop: "1px solid #e0e0e0",
            backgroundColor: "#f5f5f5",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {filteredOrders.length !== orders.length
              ? `Hiển thị ${filteredOrders.length} / ${orders.length} đơn hàng`
              : `Tổng cộng: ${orders.length} đơn hàng`}
            {isLoading && (
              <CircularProgress size={12} sx={{ ml: 1, verticalAlign: "middle" }} />
            )}
          </Typography>
        </Box>

      </Paper>

      {/* Snackbar Alert */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={4000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={alertSeverity}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default NhanhOrders;
